import { List, ListGroup } from 'framework7-react';
import { FC, Fragment, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import pluralize from 'pluralize';

import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { FeedEvent, useEventQuery, useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useSeriesByTagSlug } from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useRespondToEntertainerEventRequestMutation } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';

import { useNavigate, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { findFirstAlphaCharacter } from '@utilities/findFirstAlphaCharacter';
import { getInstagramDmLink } from '@utilities/getInstagramDmLink';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import EventCard from '@components/cards/EventCard';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { LetterAvatar } from '@components/layout/LetterAvatar/LetterAvatar';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';

export const ShowListItem = ({ event: feedEvent }: { event: FeedEvent }) => {
  const { navigate } = useRouter();
  const { event } = useEventQuery(feedEvent._id);
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();

  if (!event) {
    return <></>;
  }

  const pendingEntertainers =
    event?.entertainers?.filter(
      (entertainer) =>
        (entertainer as any).status === 'pending' && entertainer._id !== currentEntertainer?._id,
    ) ?? [];

  return (
    <li className="media-item" onClick={() => navigate(`/events/${event._id}`)}>
      <div className="item-content">
        <div className="item-inner">
          <div className="d-flex align-items-center">
            <div style={{ overflow: 'hidden' }}>
              <div className="item-text" style={{ fontSize: 14, lineHeight: '1.1' }}>
                {event.timeDetails}
              </div>
              <div className="item-title-row">
                <div className="item-title">{event.title}</div>
              </div>
              <div className="item-footer">
                <div className="d-grid gap-1">
                  <div>
                    {pendingEntertainers?.length || pendingEntertainers
                      ? `${pendingEntertainers.length} pending ${pluralize(
                          'entertainer',
                          pendingEntertainers.length,
                        )} ($${(pendingEntertainers.length * 5).toFixed(2)})`
                      : 'No pending entertainers'}
                  </div>
                </div>
              </div>
            </div>

            <div className="ml-auto ps-1">
              <div className="d-flex gap-1 align-items-center">
                <div>
                  <FontAwesomeIcon name="chevron-right" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export const EventLineupReferralInstructionsScreen = createScreenComponent<{ tagSlug: string }>(
  ({ tagSlug }) => {
    const { track } = useAnalytics();
    const navigate = useNavigate();

    const { isFetched: isSeriesReady, series } = useSeriesByTagSlug(tagSlug);

    const { createAuthGateHandler } = useAuthGate();
    const { openConfirmDialog } = useDialog();
    const { currentUser } = useCurrentUser();

    const { eventsAreReady: seriesEventsAreReady, events: existingSeriesEvents = [] } =
      useUnauthorizedEventsQuery(
        {
          startDate: dayjs().format('YYYY-MM-DD'),
          rsvpData: 'ignore',
          forceSupply: 'true',
          tagIds: [series?._id ?? ''],
        },
        Boolean(series?._id),
      );

    const eligibleEvents = useMemo(() => {
      return existingSeriesEvents.filter((event) => {
        return (event as any)?.referralUrls?.[currentUser?._id ?? ''];
      });
    }, [existingSeriesEvents, currentUser?._id]);

    const handleCreateAnAccount = () => {
      track('Create An Account');
      createAuthGateHandler(() => {}, {
        entertainerCallback: () => {},
        cityId: series?.city?._id,
      });
    };

    const handleExistingAccount = () => {
      track('Log In Existing Account');
      createAuthGateHandler(() => {});
    };

    return (
      <Screen
        name="Lineup Referral Screen"
        headerProps={{
          title: 'Lineup Confirmations',
        }}
        hideToolbar
        disableLocationPermissionGate
      >
        <Screen.Content maxWidth={425}>
          <TitleToolbar className="mb-0" text="Help Us Keep The Calendar Accurate" size="md" />
          <p className="mt-0">
            <em>Earn $5 per confirmed cast member</em>
          </p>

          <div className="markdown-block">
            <p>
              Colorado Drag Guide exists to make sure local drag shows are easy to find and accurate
              for performers, fans, and allies alike.
            </p>
            <p>
              We actively track down drag shows across Colorado and update lineups based on official
              posters and announcements, but we're only human and sometimes miss things.
            </p>
            <p>
              <strong>That's where you come in!</strong>
            </p>
            <p>
              We're asking you to help us bridge that gap by inviting cast members to confirm their
              spot directly.
            </p>
            <p>
              For every show, you'll receive a personal referral link to share with performers, and
              for every performer who joins through your link, you earn $5.
            </p>
            <TitleToolbar text="🔥 Why this matters" size="sm" />
            <p>Every confirmed lineup helps:</p>
            <ul>
              <li>Drag fans find shows that match their vibe</li>
              <li>New audience members show up informed and excited</li>
              <li>Performers are easier to find and credit</li>
              <li>Smaller and emerging artists get more of a spotlight</li>
            </ul>
            <p>This is about making it even easier to support local drag.</p>
          </div>

          {currentUser ? (
            <>
              <hr style={{ marginTop: 48 }} />
              <TitleToolbar text="Eligible Shows" size="md" />
              {eligibleEvents.length ? (
                <List className="mt-0" mediaList style={{ marginLeft: -16, marginRight: -16 }}>
                  <ListGroup>
                    {eligibleEvents.map((event) => (
                      <ShowListItem key={event._id} event={event} />
                    ))}
                  </ListGroup>
                </List>
              ) : (
                <EmptyState
                  title="No eligible shows"
                  text="We don't have any shows ready for you. Send us a message and we'll line something up!"
                  button={{
                    text: 'Contact Us',
                    onClick: () => {
                      track('Contact from Lineup Referral Instructions Screen');
                      openExternalUrl(getInstagramDmLink('coloradodragguide'));
                    },
                  }}
                />
              )}
            </>
          ) : (
            <>
              <p>Let's get started:</p>
              <div className="d-grid gap-1 mt-3">
                <Button roundness="rounded" onClick={handleCreateAnAccount}>
                  Create an account
                </Button>
                <Button
                  roundness="rounded"
                  variant="flat"
                  color="neutral"
                  onClick={handleExistingAccount}
                >
                  I already have an account
                </Button>
              </div>
            </>
          )}
        </Screen.Content>
      </Screen>
    );
  },
);
